/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, ActionMenu } from '../../design-system';
import { Input, Select, message, Modal as AntModal, Space, Tag, Tooltip } from 'antd';
import { SearchOutlined, CloseCircleOutlined, UserOutlined, CheckCircleOutlined, FilterOutlined } from '@ant-design/icons';
import { userService } from '../../services/userService';
import roleService from '../../service/RoleService';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { TERMINATE_REASON_LABELS } from '../../utils/enumNormalizer';
import AuthenticatedAvatar from '../Common/AuthenticatedAvatar';

const getExitImpactScoreLabel = (score) => {
    if (score <= 3) return 'Low';
    if (score <= 5) return 'Medium';
    return 'High';
};

const getExitImpactScoreColor = (score) => {
    if (score <= 3) return 'green';
    if (score <= 5) return 'gold';
    return 'red';
};

const EXIT_IMPACT_PERFORMANCE_LABELS = {
    high: 'High (+3)',
    average: 'Average (+2)',
    low: 'Low (+1)'
};

const EXIT_IMPACT_REPLACEMENT_LABELS = {
    easy: 'Easy (+0)',
    medium: 'Medium (+1)',
    difficult: 'Difficult (+2)'
};

const getExitImpactScoreTooltip = (scoreData, score) => (
    <div>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
            {score} / 7 - {getExitImpactScoreLabel(score)} Impact
        </div>
        <div>Rehire: {scoreData.rehire ? 'Yes (+2)' : 'No (0)'}</div>
        <div>Performance: {EXIT_IMPACT_PERFORMANCE_LABELS[scoreData.performance] || '-'}</div>
        <div>Replacement: {EXIT_IMPACT_REPLACEMENT_LABELS[scoreData.replacementEase] || '-'}</div>
        {scoreData.reasonLeaving && (
            <div style={{ marginTop: 6 }}>Reason: {scoreData.reasonLeaving}</div>
        )}
    </div>
);

const DeactivatedUserList = () => {
    const navigate = useNavigate();
    const canManageDeactivatedUsers = roleService.isHR() || roleService.isManager();
    const canDeleteDeactivatedUsers = roleService.isHR();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [search, setSearch] = useState('');
    const [officeFilter, setOfficeFilter] = useState(null);
    const [departmentFilter, setDepartmentFilter] = useState(null);
    const [terminationReasonFilter, setTerminationReasonFilter] = useState(null);
    const [sortConfig, setSortConfig] = useState({ field: null, order: null });
    const [dropdowns, setDropdowns] = useState({ offices: [], departments: [] });

    useEffect(() => {
        loadDropdowns();
    }, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        loadUsers();
    }, [pagination.current, pagination.pageSize, search, officeFilter, departmentFilter, terminationReasonFilter, sortConfig.field, sortConfig.order]);

    const loadDropdowns = async () => {
        try {
            const data = await userService.getDropdowns();
            setDropdowns(data);
        } catch (error) {
            message.error('Failed to load dropdown data');
        }
    };

    const loadUsers = async () => {
        setLoading(true);
        try {
            const result = await userService.getUsers({
                page: pagination.current,
                limit: pagination.pageSize,
                search,
                office_id: officeFilter,
                department_id: departmentFilter,
                termination_reason: terminationReasonFilter,
                sortBy: sortConfig.field,
                sortOrder: sortConfig.order === 'ascend' ? 'asc' : sortConfig.order === 'descend' ? 'desc' : null,
                is_active: false // Only deactivated users
            });

            setUsers(result.users || []);
            setPagination(prev => ({ ...prev, total: result.pagination?.total || 0 }));
        } catch (error) {
            message.error('Failed to load deactivated users');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (user) => {
        navigate(`/admin/hr/employees/edit/${user.id}`);
    };

    const handleReactivate = (user) => {
        AntModal.confirm({
            title: 'Reactivate User',
            content: `Are you sure you want to reactivate "${user.full_name}"?`,
            okText: 'Reactivate',
            okType: 'primary',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await userService.activateUser(user.id);
                    message.success('User reactivated successfully');
                    loadUsers();
                } catch (error) {
                    message.error(getApiErrorMessage(error, 'Failed to reactivate user'));
                }
            }
        });
    };

    const handleDelete = (user) => {
        AntModal.confirm({
            title: 'Delete User',
            content: (
                <div>
                    <p>Are you sure you want to <strong>permanently delete</strong> "{user.full_name}"?</p>
                    <p style={{ color: '#ff4d4f', marginTop: 8 }}>
                        <strong>Warning:</strong> This action cannot be undone. All user data will be permanently removed.
                    </p>
                </div>
            ),
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await userService.deleteUser(user.id);
                    message.success('User deleted successfully');
                    loadUsers();
                } catch (error) {
                    message.error(getApiErrorMessage(error, 'Failed to delete user'));
                }
            }
        });
    };

    const handleSearch = (value) => {
        setSearch(value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleOfficeChange = (value) => {
        setOfficeFilter(value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleDepartmentChange = (value) => {
        setDepartmentFilter(value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleTerminationReasonChange = (value) => {
        setTerminationReasonFilter(value);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const resetFilters = () => {
        setSearch('');
        setOfficeFilter(null);
        setDepartmentFilter(null);
        setTerminationReasonFilter(null);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const terminationReasonOptions = Object.entries(TERMINATE_REASON_LABELS).map(([value, label]) => ({
        value,
        label
    }));

    const getColumnSortOrder = (field) => (sortConfig.field === field ? sortConfig.order : null);

    const columns = [
        {
            title: 'Name',
            dataIndex: 'full_name',
            key: 'full_name',
            width: '20%',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            sortOrder: getColumnSortOrder('full_name'),
            render: (text, record) => (
                <Space size="small">
                    <AuthenticatedAvatar
                        src={record.profile_picture}
                        alt={record.full_name}
                        icon={<UserOutlined />}
                        size={32}
                        style={{ backgroundColor: '#999', opacity: 0.6 }}
                    />
                    <span
                        style={{ color: '#999', cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => navigate(`/profile/${record.id}`)}
                        onMouseEnter={(e) => e.target.style.color = '#FED230'}
                        onMouseLeave={(e) => e.target.style.color = '#999'}
                    >
                        {text}
                    </span>
                </Space>
            )
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            width: '22%',
            render: (text) => <span style={{ color: '#999' }}>{text}</span>
        },
        {
            title: 'Office',
            dataIndex: ['office', 'full_name'],
            key: 'office',
            width: '10%',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            sortOrder: getColumnSortOrder('office'),
            render: (text) => <span style={{ color: '#999' }}>{text || 'All (owner)'}</span>
        },
        {
            title: 'Billable Rate Card',
            dataIndex: ['user_role', 'name'],
            key: 'user_role',
            width: '18%',
            render: (text) => <span style={{ color: '#999' }}>{text || '-'}</span>
        },
        {
            title: 'Termination Date',
            dataIndex: 'job_termination_date',
            key: 'job_termination_date',
            width: '10%',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            sortOrder: getColumnSortOrder('job_termination_date'),
            render: (date) => {
                if (!date) return <span style={{ color: '#999' }}>-</span>;
                const formattedDate = new Date(date).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                });
                return <span style={{ color: '#999' }}>{formattedDate}</span>;
            }
        },
        {
            title: 'Termination Reason',
            dataIndex: 'termination_reason',
            key: 'termination_reason',
            width: '15%',
            render: (reason) => <span style={{ color: '#999' }}>{reason || '-'}</span>
        },
        {
            title: 'Exit Impact Score',
            dataIndex: 'exit_impact_score',
            key: 'exit_impact_score',
            width: '12%',
            sorter: true,
            sortDirections: ['descend', 'ascend'],
            sortOrder: getColumnSortOrder('exit_impact_score'),
            render: (scoreData) => {
                const score = scoreData?.totalScore;
                if (score === null || score === undefined) {
                    return <span style={{ color: '#999' }}>-</span>;
                }

                return (
                    <Tooltip title={getExitImpactScoreTooltip(scoreData, score)}>
                        <Tag color={getExitImpactScoreColor(score)} style={{ cursor: 'help' }}>
                            {score}
                        </Tag>
                    </Tooltip>
                );
            }
        },
        {
            title: '',
            key: 'actions',
            align: 'center',
            width: 80,
            fixed: 'right',
            render: (_, record) => {
                // Build menu items based on current user's permissions
                const menuItems = [];

                // HR and scoped managers can manage visible deactivated users.
                if (!canManageDeactivatedUsers) {
                    return '-';
                }

                // EDIT: ROLE_HR only (stricter than active users)
                menuItems.push('edit');

                // REACTIVATE: ROLE_HR
                menuItems.push({
                    key: 'reactivate',
                    label: 'Reactivate',
                    icon: <CheckCircleOutlined style={{ color: '#4CAF50' }} />,
                    color: '#4CAF50'
                });

                // DELETE: ROLE_HR + (created within 24 hours OR non-production)
                // Check if user was created within last 24 hours
                const createdAt = record.created_at ? new Date(record.created_at) : null;
                const now = new Date();
                const hoursSinceCreation = createdAt ? (now - createdAt) / (1000 * 60 * 60) : 999;
                const isProduction = process.env.NODE_ENV === 'production';
                const canDelete = canDeleteDeactivatedUsers && (!isProduction || hoursSinceCreation < 24);

                if (canDelete) {
                    menuItems.push({ type: 'divider' });
                    menuItems.push('delete');
                }

                return (
                    <ActionMenu
                        items={menuItems}
                        record={record}
                        onAction={(key, record) => {
                            if (key === 'edit') {
                                handleEdit(record);
                            } else if (key === 'reactivate') {
                                handleReactivate(record);
                            } else if (key === 'delete') {
                                handleDelete(record);
                            }
                        }}
                    />
                );
            }
        }
    ];

    const handleTableChange = (nextPagination, _filters, sorter) => {
        setPagination(prev => ({
            ...prev,
            current: nextPagination.current,
            pageSize: nextPagination.pageSize,
            total: prev.total
        }));

        if (!Array.isArray(sorter)) {
            setSortConfig({
                field: sorter?.order ? (sorter?.columnKey || sorter?.field || null) : null,
                order: sorter?.order || null
            });
        }
    };

    return (
        <div>
            {/* Header with Filters */}
            <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <Space size="small">
                        <Select
                            className="yellow-dropdown"
                            placeholder="Filter by Office"
                            style={{ minWidth: 200 }}
                            value={officeFilter}
                            onChange={handleOfficeChange}
                            allowClear
                            options={[
                                { label: 'All Offices', value: null },
                                ...dropdowns.offices.map(office => ({
                                    label: office.full_name,
                                    value: office.id
                                }))
                            ]}
                        />

                        <Select
                            className="yellow-dropdown"
                            placeholder="Filter by Department"
                            style={{ minWidth: 200 }}
                            value={departmentFilter}
                            onChange={handleDepartmentChange}
                            allowClear
                            options={[
                                { label: 'All Departments', value: null },
                                ...dropdowns.departments.map(dept => ({
                                    label: dept.name,
                                    value: dept.id
                                }))
                            ]}
                        />

                        <Select
                            className="yellow-dropdown"
                            placeholder="Filter by Termination Reason"
                            style={{ minWidth: 220 }}
                            value={terminationReasonFilter}
                            onChange={handleTerminationReasonChange}
                            allowClear
                            options={[
                                { label: 'All Termination Reasons', value: null },
                                ...terminationReasonOptions
                            ]}
                        />

                        <FilterOutlined
                            style={{
                                color: search || officeFilter || departmentFilter || terminationReasonFilter ? '#FED230' : '#999',
                                marginRight: 8,
                                cursor: 'pointer',
                                fontSize: 16
                            }}
                            onClick={resetFilters}
                            title="Reset filters"
                        />
                    </Space>

                    <Input
                        placeholder="Search by name or email"
                        style={{ width: 220 }}
                        value={search}
                        onChange={(e) => handleSearch(e.target.value)}
                        prefix={<SearchOutlined style={{ color: "rgba(0,0,0,.25)" }} />}
                        suffix={
                            search ? (
                                <CloseCircleOutlined
                                    style={{
                                        color: "rgba(0,0,0,.25)",
                                        cursor: "pointer",
                                    }}
                                    onClick={() => handleSearch("")}
                                />
                            ) : null
                        }
                    />
                </div>
            </div>

            {/* Table */}
            <Table
                columns={columns}
                dataSource={users}
                loading={loading}
                rowKey="id"
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} deactivated users`,
                    pageSizeOptions: ['10', '20', '50', '100']
                }}
                onChange={handleTableChange}
                scroll={{ x: 1400 }}
                variant="standard"
                bordered
            />
        </div>
    );
};

export default DeactivatedUserList;
